import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// Status tiles: color + icon + label, never color alone
const STATUS_TILES = [
  {
    key: "breached",
    label: "SLA breached",
    color: "var(--red-500)",
    icon: <path d="M12 8v4m0 4h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />,
  },
  {
    key: "dueSoon",
    label: "Due soon",
    color: "var(--amber-500)",
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 2" />
      </>
    ),
  },
  {
    key: "onTrack",
    label: "On track",
    color: "var(--green-500)",
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8 12 3 3 5-6" />
      </>
    ),
  },
];

const SEVERITY_LABELS = { critical: "Critical", high: "High", medium: "Medium", low: "Low" };

/**
 * Dashboard card: remediation SLA status of open findings and open-finding aging.
 */
const SlaDashboardCard = () => {
  const [summary, setSummary] = useState(null);
  const [hoveredBucket, setHoveredBucket] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    const apiBase = process.env.REACT_APP_DEFAULTPATH || "http://localhost:1337/";

    fetch(`${apiBase}sla/summary`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && data.status) setSummary(data);
      })
      .catch(() => {});
  }, []);

  if (!summary) return null;

  const maxAging = Math.max(1, ...summary.aging.map((b) => b.count));
  const breachedSeverities = Object.entries(summary.breachedBySeverity).filter(([, n]) => n > 0);

  return (
    <div className="asm-card asm-sla-card">
      <div className="asm-card-header asm-sla-header">
        <span>Remediation SLA</span>
        <Link to="/findings" className="asm-sla-link">View findings →</Link>
      </div>

      <div className="asm-sla-body">
        {/* SLA status tiles */}
        <div className="asm-sla-tiles">
          {STATUS_TILES.map((t) => (
            <div className="asm-sla-tile" key={t.key}>
              <div className="asm-sla-tile-label">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={t.color} strokeWidth="2.2"
                  strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  {t.icon}
                </svg>
                {t.key === "dueSoon" ? `Due in ${summary.dueSoonDays} days` : t.label}
              </div>
              <div className="asm-sla-tile-value">{summary[t.key]}</div>
              {t.key === "breached" && (
                <div className="asm-sla-tile-sub">
                  {breachedSeverities.length
                    ? breachedSeverities.map(([sev, n]) => `${SEVERITY_LABELS[sev]} ${n}`).join(" · ")
                    : "No overdue findings"}
                </div>
              )}
            </div>
          ))}

          <div className="asm-sla-tile">
            <div className="asm-sla-tile-label">SLA compliance</div>
            <div className="asm-sla-tile-value">
              {summary.compliancePct === null ? "—" : `${summary.compliancePct}%`}
            </div>
            <div className="asm-sla-tile-sub">
              {summary.openTotal} open finding{summary.openTotal === 1 ? "" : "s"}
              {summary.acceptedRiskActive > 0 && ` · ${summary.acceptedRiskActive} risk accepted`}
            </div>
          </div>
        </div>

        {/* Open finding aging (single series, one hue) */}
        <div className="asm-sla-aging">
          <div className="asm-sla-aging-title">Open finding age (since first seen)</div>
          <div className="asm-sla-aging-chart" role="img"
            aria-label={`Open findings by age: ${summary.aging.map((b) => `${b.label} ${b.count}`).join(", ")}`}>
            {summary.aging.map((b) => (
              <div
                key={b.key}
                className={`asm-sla-aging-col ${hoveredBucket === b.key ? "is-hovered" : ""}`}
                onMouseEnter={() => setHoveredBucket(b.key)}
                onMouseLeave={() => setHoveredBucket(null)}
              >
                {hoveredBucket === b.key && (
                  <div className="asm-sla-aging-tip">
                    <strong>{b.count}</strong> open finding{b.count === 1 ? "" : "s"} · {b.label}
                  </div>
                )}
                <div className="asm-sla-aging-count">{b.count}</div>
                <div className="asm-sla-aging-track">
                  {b.count > 0 && (
                    <div className="asm-sla-aging-bar" style={{ height: `${(b.count / maxAging) * 100}%` }} />
                  )}
                </div>
                <div className="asm-sla-aging-label">{b.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SlaDashboardCard;
